import dynamic from 'next/dynamic';
const Lightbox = dynamic(() => import('react-spring-lightbox'));

import { useAtom } from 'jotai';
import styled, { useTheme, useViewportWidth } from '@xstyled/styled-components';
import useLockBodyScroll from 'hooks/UI/useLockBodyScroll';
import { currentGalleryIndexAtom, lightboxAtom } from 'global/store';
import { Close, SliderArrowLeft, SliderArrowRight } from '@/UI/Icons';

const Header = styled.div`
  position: relative;
  padding: 1rem 1rem 2rem;

  span {
    position: absolute;
    top: 1.5rem;
    right: 0.5rem;
    z-index: 11;
    cursor: pointer;

    @media (min-width: desktop) {
      top: 2rem;
      right: 3rem;

      svg {
        transform: scale(1.5);
      }
    }
  }
`;

const Arrow = styled.spanBox`
  position: absolute;
  top: 50%;
  z-index: 2;
  cursor: pointer;

  @media (max-width: desktop) {
    display: none;
  }
`;

export default function Gallery({ images }) {
  const galleryImages = images?.map(img => ({
    src: img.url,
    alt: img.label
  }));

  const theme = useTheme();
  const viewportWidth = useViewportWidth();

  const [isLightboxOpen, setLightboxOpen] = useAtom(lightboxAtom);
  const [currentIndex, setCurrentIndex] = useAtom(currentGalleryIndexAtom);

  const goToPrev = () => currentIndex > 0 && setCurrentIndex(currentIndex - 1);
  const goToNext = () =>
    currentIndex + 1 < galleryImages.length &&
    setCurrentIndex(currentIndex + 1);

  useLockBodyScroll(isLightboxOpen);

  return (
    <Lightbox
      images={galleryImages}
      isOpen={isLightboxOpen}
      currentIndex={currentIndex}
      onPrev={goToPrev}
      onNext={goToNext}
      onClose={() => setLightboxOpen(false)}
      singleClickToZoom
      style={{
        background: theme.colors['gray-300'],
        padding: viewportWidth < 1024 && '0 1rem'
      }}
      renderHeader={() => (
        <Header>
          <span
            role="button"
            aria-label="Close image gallery"
            onClick={() => setLightboxOpen(false)}
          >
            <Close />
          </span>
        </Header>
      )}
      renderPrevButton={() => (
        <>
          {currentIndex > 0 && (
            <Arrow
              role="button"
              aria-label="Go to previous image"
              left="2rem"
              onClick={goToPrev}
            >
              <SliderArrowLeft strokeWidth={4} />
            </Arrow>
          )}
        </>
      )}
      renderNextButton={() => (
        <>
          {currentIndex + 1 < galleryImages.length && (
            <Arrow
              role="button"
              aria-label="Go to next image"
              right="2rem"
              onClick={goToNext}
            >
              <SliderArrowRight strokeWidth={4} />
            </Arrow>
          )}
        </>
      )}
      pageTransitionConfig={{
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: { mass: 1, tension: 320, friction: 32 }
      }}
    />
  );
}
